/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Input } from 'reactstrap';
import { loadCustomMetricGroupsByInitiativeId, reloadCustomMetricGroupsByInitiativeId } from '../../actions/initiative';
import { useAddCompaniesToMetricGroupMutation } from '../../api/metric-groups';
import { ROUTES } from '../../constants/routes';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import { loadPortfolioCompanies } from '../../slice/portfolioCompanySlice';
import { loadPortfolioById } from '../../slice/portfolioSlice';
import { InitiativeCompany } from '../../types/initiative';
import { MetricGroup } from '../../types/metricGroup';
import { naturalSort } from '../../utils';
import { BasicAlert } from '../alert/BasicAlert';
import Loader from '../loader';
import { getAccessHint } from '../portfolio/exchange-view/partials/access-status';
import SimpleTooltip from '../simple-tooltip';
import Table, { TableColumn } from '../table/Table';
import { PackAssigmentFilters, PackAssignmentFilters } from './PackAssignmentFilters';
import { PAGE_SIZE } from './utils';
import { DataScopeAccess } from '../../types/dataShare';
import { useSiteAlert } from '../../hooks/useSiteAlert';
import { getViewAccess } from '../../utils/dataShare';

interface ApplyFilterCompanies {
  companies: InitiativeCompany[];
  searchText: string;
  filters: PackAssigmentFilters;
}

interface PackAssignmentTableProps {
  metricGroup?: MetricGroup;
}

const applyFilterCompanies = ({ companies, searchText, filters }: ApplyFilterCompanies): InitiativeCompany[] => {
  return companies.filter((company) => {
    const nameCondition = searchText ? company.name.toLowerCase().includes(searchText.trim().toLowerCase()) : true;
    const sectorCondition = filters.sector ? company.sectorText === filters.sector : true;
    const industryCondition = filters.industry ? company.industryText === filters.industry : true;
    const dataShareStatusCondition = filters.dataShareStatus ? getViewAccess(company) === filters.dataShareStatus : true;
    return nameCondition && sectorCondition && industryCondition && dataShareStatusCondition;
  });
};

export const PackAssignmentTable = ({ metricGroup }: PackAssignmentTableProps) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { addSiteAlert, addSiteError } = useSiteAlert();

  const { portfolioId } = useParams<{ portfolioId: string }>();
  const { portfolio, companies } = useAppSelector((state) => ({
    portfolio: state.portfolio.data,
    companies: state.portfolioCompany.data,
  }));

  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<PackAssigmentFilters>({});
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const filteredCompanies = useMemo(
    () => (companies && !!companies.length ? applyFilterCompanies({ companies, searchText, filters }) : []),
    [companies, searchText, filters]
  );

  const hasFilters = Boolean(searchText || Object.values(filters).some((item) => Boolean(item)));

  // shareIds contains all metricGroup's assigned companies, including the company that was removed from portfolio
  const sharedIds = useMemo(() => metricGroup?.share?.map((shared) => shared.initiativeId) ?? [], [metricGroup]);

  // getting a list of non-shared company's ids
  const nonSharedIds = useMemo(
    () =>
      filteredCompanies.reduce((acc, company) => {
        if (!sharedIds.includes(company._id)) {
          acc.push(company._id);
        }
        return acc;
      }, [] as string[]) ?? [],
    [filteredCompanies, sharedIds]
  );

  const [addCompaniesToMetricGroup] = useAddCompaniesToMetricGroupMutation();

  useEffect(() => {
    if (!portfolioId) {
      return;
    }
    dispatch(loadPortfolioById(portfolioId));
    dispatch(loadPortfolioCompanies(portfolioId));
    dispatch(loadCustomMetricGroupsByInitiativeId(portfolioId));
  }, [dispatch, portfolioId]);

  if (!portfolio || !metricGroup) {
    return <Loader />;
  }

  const handleReload = () => {
    dispatch(reloadCustomMetricGroupsByInitiativeId(portfolio._id));
  };

  const handleClickCompany = (initiativeId: string) => {
    history.push(
      generateUrl(ROUTES.PORTFOLIO_TRACKER_COMPANY, { portfolioId: portfolio._id, companyId: initiativeId })
    );
  };

  const handleToggleCompany = async (currentCompanyId: string) => {
    const currentSelectedCompanies = selectedCompanies.includes(currentCompanyId)
      ? selectedCompanies.filter((id) => id !== currentCompanyId)
      : [...selectedCompanies, currentCompanyId];
    setSelectedCompanies(currentSelectedCompanies);
  };

  const addSelectedCompaniesToMetricGroup = async () => {
    addCompaniesToMetricGroup({
      initiativeId: portfolio._id,
      groupId: metricGroup._id,
      initiativeIds: selectedCompanies,
    })
    .unwrap()
      .then(() => {
        addSiteAlert({ content: 'The pack has been sent' });
        setSelectedCompanies([]);
        handleReload();
      })
      .catch((e) => {
        addSiteError(e);
      });
  };

  const isSelectAll =
    nonSharedIds.length === 0 || (selectedCompanies.length && selectedCompanies.length === nonSharedIds.length);

  const toggleSelectAll = () => {
    if (isSelectAll) {
      return setSelectedCompanies([]);
    }

    setSelectedCompanies(nonSharedIds);
  };

  const tableColumns: TableColumn<InitiativeCompany>[] = [
    {
      accessor: '_id',
      Header: () => 'Access',
      cellProps: {
        className: 'text-center',
        style: {
          minWidth: 90
        }
      },
      Cell: (c) => {
        const company = c.row.original;
        switch (getViewAccess(company)) {
          case DataScopeAccess.Full:
            return (
              <SimpleTooltip text={getAccessHint(company)}>
                <i className='text-ThemeIconDark fa-solid fa-circle fa-lg' />
              </SimpleTooltip>
            );
          case DataScopeAccess.Partial:
            return (
              <SimpleTooltip text={getAccessHint(company)}>
                <i className='text-ThemeIconDark fa-solid fa-circle-half-stroke fa-lg' />
              </SimpleTooltip>
            );
          case DataScopeAccess.None:
            return (
              <SimpleTooltip text={getAccessHint(company)}>
                <i className='text-ThemeIconDark fa-regular fa-circle fa-lg' />
              </SimpleTooltip>
            );

          default:
            return <></>;
        }
      },
      disableSortBy: true,
    },
    {
      accessor: 'name',
      Header: () => 'Company name',
      cellProps: {
        style: {
          minWidth: 200,
        }
      },
      Cell: (c) => {
        const company = c.row.original;
        return (
          <Button color='link' className='text-left' onClick={() => handleClickCompany(company._id)}>
            {company.name}
          </Button>
        );
      },
      sortType: (a, b) => naturalSort(a.original.name, b.original.name),
    },
    {
      accessor: 'sectorText',
      Header: () => 'Sector',
      cellProps: {
        className: 'text-left',
        style: {
          minWidth: 200,
          maxWidth: 200,
        }
      },
      sortType: (a, b) => naturalSort(a.original.name, b.original.name),
    },
    {
      accessor: 'industryText',
      Header: () => 'Industry',
      cellProps: {
        className: 'text-left',
        style: {
          minWidth: 200,
          maxWidth: 200,
        }
      },
      sortType: (a, b) => naturalSort(a.original.name, b.original.name),
    },
    {
      accessor: 'select',
      Header: () => (
        <Button color='link' onClick={toggleSelectAll} disabled={nonSharedIds.length === 0}>
          {isSelectAll ? 'Deselect all' : 'Select all'}
        </Button>
      ),
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => {
        const currentCompanyId = c.row.original._id;
        return (
          <Input
            type='checkbox'
            defaultChecked={sharedIds.includes(currentCompanyId) || selectedCompanies.includes(currentCompanyId)}
            disabled={sharedIds.includes(currentCompanyId)}
            className='mr-2'
            onChange={() => handleToggleCompany(currentCompanyId)}
          />
        );
      },
      disableSortBy: true,
    },
  ];

  return (
    <>
      <PackAssignmentFilters
        changeFilters={setFilters}
        changeSearchText={setSearchText}
        filters={filters}
        searchText={searchText}
      />
      <div className='pack-assigment-table-container mt-3'>
        <div className='table-wrapper mt-2'>
          <Table
            responsive
            className='pack-assigment-table'
            columns={tableColumns}
            data={filteredCompanies}
            pageSize={PAGE_SIZE}
          />
          {filteredCompanies?.length === 0 ? (
            <BasicAlert type='secondary' className='mt-2 ml-0'>
              {hasFilters ? 'No company match your criteria' : 'You have no companies assigned to this portfolio'}
            </BasicAlert>
          ) : null}
        </div>
        <div className='text-right mt-5'>
          <Button onClick={addSelectedCompaniesToMetricGroup} disabled={selectedCompanies.length === 0}>
            Assign pack to companies
          </Button>
        </div>
      </div>
    </>
  );
};
